
import React, { useState } from 'react';
import { CheckIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/auth';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/context/language-context';

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface FeatureItem {
  text: string;
  included: boolean;
}

interface PlanProps {
  title: string;
  price: string;
  description: string;
  features: FeatureItem[];
  buttonText: string;
  onSubscribe: () => void;
  isLoading: boolean;
  recommended?: boolean;
  badge?: string;
}

const PlanCard: React.FC<PlanProps> = ({
  title,
  price,
  description,
  features,
  buttonText,
  onSubscribe,
  isLoading,
  recommended = false,
  badge,
}) => {
  return (
    <Card className={`flex flex-col h-full transition-all duration-300 ${
      recommended ? 'border-primary shadow-lg scale-105 z-10' : 'hover:border-primary/50 hover:shadow-md'
    }`}>
      <CardHeader className="relative pb-8">
        {badge && (
          <Badge 
            className={`absolute top-2 right-2 ${
              badge === 'Élite' ? 'bg-violet-500 hover:bg-violet-600' : ''
            }`} 
            variant="default"
          >
            {badge}
          </Badge>
        )}
        <CardTitle className="text-xl font-bold">{title}</CardTitle>
        <div className="mt-2">
          <span className="text-3xl font-bold">{price}</span>
          <span className="text-sm text-muted-foreground ml-1">/mois</span>
        </div>
        <CardDescription className="mt-1">{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex-grow">
        <ul className="space-y-2">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <div className={`mr-2 mt-0.5 rounded-full p-1 ${feature.included ? 'bg-pink-100 text-pink-600' : 'bg-gray-100 text-gray-400'}`}>
                {feature.included ? (
                  <CheckIcon className="h-3 w-3" />
                ) : (
                  <div className="h-3 w-3" />
                )}
              </div>
              <span className={feature.included ? 'text-sm' : 'text-sm text-muted-foreground'}>{feature.text}</span>
            </li>
          ))}
        </ul>
      </CardContent>
      <CardFooter>
        <Button 
          className={`w-full ${
            recommended ? 'bg-pink-gradient hover:opacity-90' : ''
          }`}
          onClick={onSubscribe}
          disabled={isLoading}
        >
          {isLoading ? 'Traitement...' : buttonText}
        </Button>
      </CardFooter>
    </Card>
  );
};

const EscortSubscriptionPlans: React.FC = () => {
  const { t } = useLanguage();
  const { user, upgradeSubscription } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  const handleSubscribe = async (planId: string) => {
    if (!user) {
      navigate('/login');
      return;
    }

    setLoadingPlan(planId);
    try {
      // In a real implementation, this would connect to a payment processor
      // and then update the user's subscription tier
      await upgradeSubscription(planId as any);
      
      toast({
        title: 'Abonnement mis à jour',
        description: `Vous avez souscrit avec succès au forfait ${planId}`,
      });
    } catch (error) {
      console.error(error);
      toast({
        title: 'Échec de la mise à jour',
        description: 'Veuillez réessayer plus tard.',
        variant: 'destructive',
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  const essentialFeatures = [
    { text: 'Statut vérifié', included: true },
    { text: 'Option "Unvisible"', included: true },
    { text: 'Traduction automatique du profil', included: true },
    { text: 'Outils de création de contenu', included: true },
    { text: 'Mise en avant basique', included: true },
    { text: 'Support client standard', included: true },
    { text: 'Mise en avant avancée', included: false },
    { text: 'Vente de médias privés', included: false },
    { text: 'Gestion automatique des prospects', included: false },
    { text: 'Prise de rendez-vous intégrée', included: false },
    { text: 'Mise en avant premium', included: false },
    { text: 'Abonnement pour échanges vocaux par IA', included: false },
    { text: 'Panic button', included: false },
    { text: 'Analyse de données', included: false }
  ];

  const premiumFeatures = [
    { text: 'Statut vérifié', included: true },
    { text: 'Option "Unvisible"', included: true },
    { text: 'Traduction automatique du profil', included: true },
    { text: 'Outils de création de contenu', included: true },
    { text: 'Mise en avant basique', included: true },
    { text: 'Support client prioritaire', included: true },
    { text: 'Mise en avant avancée', included: true },
    { text: 'Vente de médias privés et services additionnels', included: true },
    { text: 'Gestion automatique des prospects', included: true },
    { text: 'Prise de rendez-vous intégrée', included: true },
    { text: 'Mise en avant premium', included: false },
    { text: 'Abonnement pour échanges vocaux par IA', included: false },
    { text: 'Panic button', included: false },
    { text: 'Analyse de données', included: false }
  ];

  const eliteFeatures = [
    { text: 'Statut vérifié', included: true },
    { text: 'Option "Unvisible"', included: true },
    { text: 'Traduction automatique du profil', included: true },
    { text: 'Outils de création de contenu', included: true },
    { text: 'Mise en avant basique', included: true },
    { text: 'Support client VIP', included: true },
    { text: 'Mise en avant avancée', included: true },
    { text: 'Vente de médias privés et services additionnels', included: true },
    { text: 'Gestion automatique des prospects', included: true },
    { text: 'Prise de rendez-vous intégrée', included: true },
    { text: 'Mise en avant premium', included: true },
    { text: 'Abonnement pour échanges vocaux par IA', included: true },
    { text: 'Panic button', included: true },
    { text: 'Analyse de données', included: true }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <PlanCard 
        title="Essentiel"
        price="4,99€"
        description="Idéal pour les escorts indépendantes occasionnelles"
        features={essentialFeatures}
        buttonText="Choisir ce forfait"
        onSubscribe={() => handleSubscribe('essential')}
        isLoading={loadingPlan === 'essential'}
        badge="Essentiel"
      />
      
      <PlanCard 
        title="Premium"
        price="12,49€"
        description="Pour les escorts indépendantes régulières"
        features={premiumFeatures}
        buttonText="Choisir ce forfait"
        onSubscribe={() => handleSubscribe('premium')}
        isLoading={loadingPlan === 'premium'}
        recommended={true}
        badge="Premium"
      />
      
      <PlanCard 
        title="Élite"
        price="29,49€"
        description="Pour les escorts professionnelles ou agences"
        features={eliteFeatures}
        buttonText="Choisir ce forfait"
        onSubscribe={() => handleSubscribe('elite')}
        isLoading={loadingPlan === 'elite'}
        badge="Élite"
      />
    </div>
  );
};

export default EscortSubscriptionPlans;
